import { Chip } from "@mui/material";
import { HistoryOutlined } from "@mui/icons-material";

import { revisionLabelText } from "src/api/records";
import {
  citedPassageMissing,
  citedRevisionChanged,
  citedRevisionLabel,
  type PinnedReference,
} from "./citationPin";

interface Props {
  reference: PinnedReference;
  /** The reader tried to open a citation whose cited record was deleted. */
  showUnavailable?: boolean;
}

/**
 * The revision a screening citation quoted, and a note when a newer one is
 * current, the record was edited in place, the record was deleted or the
 * quoted text can no longer be located. Renders nothing for a citation made
 * before pins were recorded.
 */
export const CitationRevision = ({ reference, showUnavailable }: Props) => {
  const label = citedRevisionLabel(reference.pin);
  if (!label) return null;
  const status = reference.pin_status;
  const current =
    status?.state === "superseded" && status.current_revision_label
      ? revisionLabelText(status.current_revision_label)
      : null;
  return (
    <div
      className="llm-references__revision"
      data-testid="llm-reference-revision"
      data-pin-state={status?.state ?? "unknown"}
      style={{
        display: "flex",
        flexWrap: "wrap",
        alignItems: "center",
        gap: 6,
        margin: "2px 0 6px 28px",
        fontSize: "0.75rem",
        color: "rgba(0,0,0,.6)",
      }}
    >
      <span style={{ display: "inline-flex", alignItems: "center", gap: 4 }}>
        <HistoryOutlined fontSize="inherit" aria-hidden="true" />
        {`Cited ${label}`}
        {reference.pin?.superseded_date
          ? " (already superseded when cited)"
          : ""}
      </span>
      {status?.state === "superseded" && (
        <Chip
          size="small"
          color="info"
          variant="outlined"
          label="A newer version exists"
          data-testid="llm-reference-newer"
          sx={{ height: 22, fontSize: "0.72rem" }}
        />
      )}
      {status?.state === "unavailable" && (
        <Chip
          size="small"
          color="warning"
          variant="outlined"
          label="Source deleted"
          data-testid="llm-reference-deleted"
          sx={{ height: 22, fontSize: "0.72rem" }}
        />
      )}
      {citedRevisionChanged(reference) && (
        <Chip
          size="small"
          color="warning"
          variant="outlined"
          label="Source changed"
          data-testid="llm-reference-changed"
          sx={{ height: 22, fontSize: "0.72rem" }}
        />
      )}
      {current && <span>{`Current revision: ${current}`}</span>}
      {citedPassageMissing(reference) && (
        <span data-testid="llm-reference-passage-missing">
          The quoted text can no longer be located in this version.
        </span>
      )}
      {citedRevisionChanged(reference) && (
        <span data-testid="llm-reference-changed-note">
          The cited record has changed since it was cited.
        </span>
      )}
      {/* Rendered empty up front: a live region announces only text added
          after it is in the page. */}
      <span role="status" style={{ flexBasis: "100%", color: "#b26a00" }}>
        {showUnavailable && (
          <span data-testid="llm-reference-unavailable">
            The cited revision is no longer available: its record was deleted.
          </span>
        )}
      </span>
    </div>
  );
};

export default CitationRevision;
